import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const suppliers = (): BreadcrumbItem => ({
  labelKey: "vendors",
});

export const buildSupplierDetailsHref = (
  p: Record<string, string | undefined>,
) => (p.id ? `/supplier/${p.id}` : undefined);

/** Suppliers sidebar module routes — more specific paths first. */
const SUPPLIERS_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Suppliers list + nested
  {
    path: "/suppliers",
    crumbs: () => [suppliers(), { labelKey: "suppliersList" }],
  },
  {
    path: "/supplier/create",
    crumbs: () => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/supplier/:id/purchase-orders",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      {
        labelKey: "Details",
        href: p.id ? `/supplier/${p.id}` : undefined,
      },
      { labelKey: "Purchase Orders" },
    ],
  },
  {
    path: "/supplier/:id/bills",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      {
        labelKey: "Details",
        href: p.id ? `/supplier/${p.id}` : undefined,
      },
      { labelKey: "bills" },
    ],
  },
  {
    path: "/supplier/:id/stock-receives",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      {
        labelKey: "Details",
        href: p.id ? `/supplier/${p.id}` : undefined,
      },
      { labelKey: "Stock Received" },
    ],
  },
  {
    path: "/supplier/:id/debit-notes",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      {
        labelKey: "Details",
        href: p.id ? `/supplier/${p.id}` : undefined,
      },
      { labelKey: "Debit Notes" },
    ],
  },
  {
    path: "/supplier/:id/payments",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      {
        labelKey: "Details",
        href: p.id ? `/supplier/${p.id}` : undefined,
      },
      { labelKey: "supplierPayments" },
    ],
  },
  {
    path: "/supplier/:id",
    crumbs: () => [
      suppliers(),
      { labelKey: "suppliersList", href: "/suppliers" },
      { labelKey: "Details" },
    ],
  },

  // Purchase orders
  {
    path: "/inventory/purchase-orders",
    crumbs: () => [suppliers(), { labelKey: "Purchase Orders" }],
  },
  {
    path: "/inventory/new-purchase",
    crumbs: () => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/purchase/:id/print/supplier-bill",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/purchase/${p.id}` : undefined,
      },
      { labelKey: "bills" },
    ],
  },
  {
    path: "/inventory/purchase/:id/print/stock",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/purchase/${p.id}` : undefined,
      },
      { labelKey: "Stock Received" },
    ],
  },
  {
    path: "/inventory/purchase/:id/print/debit-note",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/purchase/${p.id}` : undefined,
      },
      { labelKey: "Debit Notes" },
    ],
  },
  {
    path: "/inventory/purchase/:id/print/unstock",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/purchase/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/purchase/:id/print",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/purchase/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/purchase/:id",
    crumbs: () => [
      suppliers(),
      { labelKey: "Purchase Orders", href: "/inventory/purchase-orders" },
      { labelKey: "Details" },
    ],
  },

  // Supplier bills
  {
    path: "/inventory/supplier-bills",
    crumbs: () => [suppliers(), { labelKey: "bills" }],
  },
  {
    path: "/inventory/new-supplier-bill/",
    crumbs: () => [
      suppliers(),
      { labelKey: "bills", href: "/inventory/supplier-bills" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/new-supplier-bill",
    crumbs: () => [
      suppliers(),
      { labelKey: "bills", href: "/inventory/supplier-bills" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/supplier-bill/:id/print",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "bills", href: "/inventory/supplier-bills" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/supplier-bill/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/supplier-bill/:id",
    crumbs: () => [
      suppliers(),
      { labelKey: "bills", href: "/inventory/supplier-bills" },
      { labelKey: "Details" },
    ],
  },

  // Stock receives
  {
    path: "/inventory/stock-receives",
    crumbs: () => [suppliers(), { labelKey: "Stock Received" }],
  },
  {
    path: "/inventory/new-stock-receives/",
    crumbs: () => [
      suppliers(),
      { labelKey: "Stock Received", href: "/inventory/stock-receives" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/new-stock-receives",
    crumbs: () => [
      suppliers(),
      { labelKey: "Stock Received", href: "/inventory/stock-receives" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/stock-receives/:id/print",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Stock Received", href: "/inventory/stock-receives" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/stock-receives/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/stock-receives/:id",
    crumbs: () => [
      suppliers(),
      { labelKey: "Stock Received", href: "/inventory/stock-receives" },
      { labelKey: "Details" },
    ],
  },

  // Debit notes
  {
    path: "/inventory/debit-note",
    crumbs: () => [suppliers(), { labelKey: "Debit Notes" }],
  },
  {
    path: "/inventory/new-debit-note",
    crumbs: () => [
      suppliers(),
      { labelKey: "Debit Notes", href: "/inventory/debit-note" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/debit-note/:id/print",
    crumbs: (p) => [
      suppliers(),
      { labelKey: "Debit Notes", href: "/inventory/debit-note" },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/debit-note/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/debit-note/:id",
    crumbs: () => [
      suppliers(),
      { labelKey: "Debit Notes", href: "/inventory/debit-note" },
      { labelKey: "Details" },
    ],
  },

  // Supplier payments
  {
    path: "/inventory/supplier-payments",
    crumbs: () => [suppliers(), { labelKey: "supplierPayments" }],
  },
  {
    path: "/inventory/new-supplier-payment/",
    crumbs: () => [
      suppliers(),
      {
        labelKey: "supplierPayments",
        href: "/inventory/supplier-payments",
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/new-supplier-payment",
    crumbs: () => [
      suppliers(),
      {
        labelKey: "supplierPayments",
        href: "/inventory/supplier-payments",
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inventory/supplier-payment/:id/print",
    crumbs: (p) => [
      suppliers(),
      {
        labelKey: "supplierPayments",
        href: "/inventory/supplier-payments",
      },
      {
        labelKey: "Details",
        href: p.id ? `/inventory/supplier-payment/${p.id}` : undefined,
      },
      { labelKey: "print" },
    ],
  },
  {
    path: "/inventory/supplier-payment/:id",
    crumbs: () => [
      suppliers(),
      {
        labelKey: "supplierPayments",
        href: "/inventory/supplier-payments",
      },
      { labelKey: "Details" },
    ],
  },
];

export const getSuppliersBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of SUPPLIERS_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
